/*
 * LanguageContext - Shared language state for the entire QR Menu V2 flow.
 * Wraps QRMenuWelcome, QRMenuPage, and all nested modals so the language
 * picker on the welcome screen propagates everywhere.
 *
 * Languages: Turkish (tr), English (en), Russian (ru), Azerbaijani (az)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';

export type QrLanguage = 'tr' | 'en' | 'ru' | 'az';

export const QR_LANGUAGES: Array<{
  code: QrLanguage;
  flag: string;
  name: string;
}> = [
    { code: 'tr', flag: 'TR', name: 'Türkçe' },
    { code: 'en', flag: 'EN', name: 'English' },
    { code: 'ru', flag: 'RU', name: 'Русский' },
    { code: 'az', flag: 'AZ', name: 'Azərbaycan' },
  ];

const STORAGE_KEY = 'qrmenu-language';

/* ---------------- Translation strings ---------------- */
export type Strings = {
  // Welcome page
  welcome: string;
  welcomeDescription: string;
  quickOrder: string;
  quickOrderDesc: string;
  specialFlavors: string;
  specialFlavorsDesc: string;
  viewMenu: string;
  table: string;

  // Header / search
  search: string;
  back: string;
  profile: string;
  openUntil: string; // "Open until 23:00"

  // Categories
  all: string;
  chefRecommendations: string;
  allFlavors: string;
  flavors: string;
  productsNotFound: string;
  sets: string;
  setMenu: string;

  // Bottom nav
  home: string;
  waiter: string;
  tracking: string;
  history: string;

  // Tracking tab
  orderTracking: string;
  orderStatus: string;
  orderNumber: string;
  preparingInKitchen: string;
  servedDone: string;
  estimated: string;
  orderReceived: string;
  preparing: string;
  served: string;
  completed: string;
  rightNow: string;
  enjoy: string;
  viewOrderDetails: string;
  noActiveOrderTrack: string;
  noActiveOrderTrackDesc: string;
  goToMenu: string;
  orderCanceled: string;
  orderCanceledDesc: string;
  pendingApprovalLabel: string;
  pendingApprovalDesc: string;
  readyLabel: string;
  readyDesc: string;
  almostReady: string;
  readyNow: string;
  itemsProgress: string;
  itemsList: string;
  itemPending: string;
  itemPreparing: string;
  itemReady: string;
  yourOrders: string;
  orderTime: string;
  refreshing: string;

  // Waiter tab
  waiterService: string;
  premiumService: string;
  seniorWaiter: string;
  reviews: string;
  rateService: string;
  feedbackOptional: string;
  send: string;
  pleaseRate: string;
  thanksForRating: string;
  callWaiter: string;
  requestBill: string;
  viewBill: string;
  specialRequest: string;
  sendRequest: string;

  // Waiter call (real API)
  waiterCallHero: string;
  waiterCallHeroDesc: string;
  waiterCallReasonTitle: string;
  waiterCallReasonSubtitle: string;
  waiterCallReasonWater: string;
  waiterCallReasonBill: string;
  waiterCallReasonHelp: string;
  waiterCallReasonCleanup: string;
  waiterCallReasonMenu: string;
  waiterCallReasonOther: string;
  waiterCallNoteLabel: string;
  waiterCallNotePlaceholder: string;
  waiterCallSubmit: string;
  waiterCallSubmitting: string;
  waiterCallCancelByCustomer: string;
  waiterCallCancelling: string;
  waiterCallClose: string;
  waiterCallStatusPending: string;
  waiterCallStatusPendingDesc: string;
  waiterCallStatusAcknowledged: string;
  waiterCallStatusAcknowledgedDesc: string;
  waiterCallNotifiedWho: (name: string) => string;
  waiterCallNoAssigned: string;
  waiterCallAcknowledgedBy: (name: string) => string;
  waiterCallAlreadyPending: string;
  waiterCallCreatedAgo: (label: string) => string;
  waiterCallTimeJustNow: string;
  waiterCallTimeSeconds: (n: number) => string;
  waiterCallTimeMinutes: (n: number) => string;
  waiterCallTimeHours: (n: number) => string;
  waiterCallNoteShown: string;
  waiterCallErrorGeneric: string;
  waiterCallErrorThrottled: string;
  waiterCallErrorNetwork: string;
  waiterCallToastSent: string;
  waiterCallToastResolved: string;
  waiterCallToastCancelled: string;
  waiterCallLiveBadge: string;
  waiterCallActionShortcut: string;

  // Bill / history tab
  bill: string;
  completePayment: string;
  table_short: string;
  today: string;
  noActiveOrder: string;
  pendingApproval: string;
  subtotal: string;
  vat: string;
  grandTotal: string;
  paymentMethod: string;
  applePayUnavailable: string;
  payWithApple: string;
  payWithCard: string;
  processing: string;
  googlePayNotConfigured: string;

  // Adisyon (live bill) - new spec
  liveBill: string;
  liveBillSubtitle: string;
  loadingBill: string;
  liveSyncedNow: string;
  itemsTotal: string;
  timeChargesTotal: string;
  discountTotal: string;
  prepaymentTotal: string;
  paymentTotal: string;
  netTotal: string;
  payable: string;
  fullyPaid: string;
  noOrdersOnTable: string;
  prepayment: string;
  partialPayment: string;
  manualDiscount: string;
  timeChargeLabel: string;
  kitchenStatusReady: string;
  kitchenStatusPreparing: string;
  kitchenStatusPending: string;
  pieces: string;

  // Phone identify
  enterPhone: string;
  enterPhoneSubtitle: string;
  phoneInputLabel: string;
  continueAction: string;
  continueAsGuest: string;
  phoneNotFound: string;
  phoneInvalidFormat: string;
  forgetMe: string;
  identifying: string;
  customerHello: (name: string) => string;

  // Loyalty section
  loyaltyTitle: string;
  loyaltyBalance: string;
  loyaltyTotalEarned: string;
  loyaltyTotalSpent: string;
  loyaltyValueIn: string;
  lifetimeStats: string;
  lifetimeOrders: string;
  lifetimeSpent: string;
  firstVisit: string;
  lastVisit: string;
  customerBalance: string;

  // History section
  historyTitle: string;
  historySubtitle: string;
  viewHistory: string;
  hideHistory: string;
  loadMore: string;
  loadingHistory: string;
  historyEmpty: string;
  paidLabel: string;
  paymentMethodCash: string;
  paymentMethodBank: string;
  paymentMethodCustomerBalance: string;
  prevPage: string;
  nextPage: string;
  pageOf: (current: number, last: number) => string;
  itemsCount: (n: number) => string;

  // Cart
  myCart: string;
  productsCount: (n: number) => string;
  emptyCartTitle: string;
  emptyCartSubtitle: string;
  total: string;
  selected: string;
  paySplit: string;
  cancelSelection: string;
  sendOrder: string;
  paySelected: string;
  cartEmpty: string;

  // Product detail modal
  chefSpecial: string;
  chefSpecialDesc: string;
  qualityIngredients: string;
  qualityIngredientsDesc: string;
  selectSize: string;
  meatType: string;
  standard: string;
  extras: string;
  oftenBoughtTogether: string;
  popular: string;
  newBadge: string;
  addToCart: string;
  itemsAddedToCart: (n: number, name: string) => string;
  itemAddedToCart: (name: string) => string;

  // Mock variants (these are user-friendly localized for mock data)
  meatChicken: string;
  meatBeef: string;
  meatLamb: string;
  extraCheese: string;
  extraAvocado: string;
  extraBacon: string;
  extraMushroom: string;

  // Profile modal
  rezmoPoints: string;
  points: string;
  pointsCallout: string;
  guest: string;
  premiumMember: string;
  signOut: string;
  signedOut: string;
  loggedInAsGoogle: string;
  loggedInAsApple: string;
  subscriptionActive: string;
  dontMissDeals: string;
  dontMissDealsDesc: string;
  continueWithGoogle: string;
  continueWithApple: string;
  connecting: string;
  or: string;
  phonePlaceholder: string;
  invalidPhone: string;
  subscribeSuccess: string;
  welcomeBack: (name: string) => string;
  darkMode: string;
  appSettings: string;
  promoCodes: string;

  // Feedback modal
  feedbackTitle: string;
  feedbackSubtitle: string;
  overallRating: string;
  satisfied: string;
  notSatisfied: string;
  selectCategory: string;
  catComplaint: string;
  catSuggestion: string;
  catFood: string;
  catService: string;
  catPayment: string;
  catOther: string;
  quickOptions: string;
  qSlowService: string;
  qColdFood: string;
  qWrongOrder: string;
  qDirtyTable: string;
  qNoise: string;
  qSmallPortion: string;
  detailedDesc: string;
  detailedPlaceholder: string;
  maxChars: string;
  feedbackEmptyError: string;
  feedbackThanks: string;

  // Notifications
  notifAddedToCart: string;
  notifWaiterCalled: string;
  notifWaiterCalledTitle: string;
  notifWaiterDesc: string;
  notifWaiterOnTheWay: string;
  notifWaiterOnTheWayDesc: string;
  notifQuantity: string;
  notifAddedToCartTitle: string;

  // Misc
  cartEmptyToast: string;
  orderSent: string;
  orderFailed: string;
  paymentLinkFailed: string;
  paymentError: string;
  paymentSuccess: string;
  applePayUnavailableMsg: string;
  applePayValidationFailed: string;
  applePayNotConfigured: string;
  applePayCannotComplete: string;
  applePayProcessFailed: string;
  applePayStartFailed: string;
  googlePayError: string;
  rezmoPointsCallout: string; // unused stub - kept for future
  wifi: string;
  showHiddenCards: string;
};

const make = (s: Strings) => s;

/* ---------------- TR ---------------- */
const tr: Strings = make({
  welcome: 'Hoş Geldiniz',
  welcomeDescription:
    'Siparişinizi doğrudan buradan verebilir, ödemenizi kolayca yapabilirsiniz.',
  quickOrder: 'Hızlı Sipariş',
  quickOrderDesc: 'Beklemeden direkt mutfağa',
  specialFlavors: 'Özel Lezzetler',
  specialFlavorsDesc: 'Şefin güncel önerileri',
  viewMenu: 'Menüyü İncele',
  table: 'MASA',

  search: 'Ara',
  back: 'Geri',
  profile: 'Profil',
  openUntil: '23:00\'a kadar açık',

  all: 'Tümü',
  chefRecommendations: 'Şefin Önerileri',
  allFlavors: 'Tüm Lezzetler',
  flavors: 'Lezzetler',
  productsNotFound: 'Ürün bulunamadı',
  sets: 'Setler',
  setMenu: 'Set Menü',

  home: 'Anasayfa',
  waiter: 'Garson',
  tracking: 'Takip',
  history: 'Hesap',

  orderTracking: 'Sipariş Takibi',
  orderStatus: 'Siparişinizin durumu',
  orderNumber: 'Sipariş No',
  preparingInKitchen: 'Mutfakta Hazırlanıyor',
  servedDone: 'Servis Edildi ✓',
  estimated: 'Tahmini',
  orderReceived: 'Sipariş Alındı',
  preparing: 'Hazırlanıyor',
  served: 'Servis Edildi',
  completed: 'Tamamlandı',
  rightNow: 'Şu an',
  enjoy: 'Afiyet olsun!',
  viewOrderDetails: 'Sipariş Detaylarını Gör',
  noActiveOrderTrack: 'Takip edilecek sipariş yok',
  noActiveOrderTrackDesc: 'Aktif siparişiniz olduğunda canlı olarak burada görünecek',
  goToMenu: 'Menüye Git',
  orderCanceled: 'Sipariş İptal Edildi',
  orderCanceledDesc: 'Bu sipariş garson tarafından iptal edildi',
  pendingApprovalLabel: 'Onay Bekliyor',
  pendingApprovalDesc: 'Sipariş garson tarafından onaylandığında mutfağa gönderilecek',
  readyLabel: 'Hazır - Servis Bekliyor',
  readyDesc: 'Mutfak hazırladı, garson masanıza getiriyor',
  almostReady: 'Hazır olmaya az kaldı',
  readyNow: 'Hazır',
  itemsProgress: 'Ürün Hazırlığı',
  itemsList: 'Sipariş İçeriği',
  itemPending: 'Beklemede',
  itemPreparing: 'Hazırlanıyor',
  itemReady: 'Hazır',
  yourOrders: 'Siparişleriniz',
  orderTime: 'Verildi',
  refreshing: 'Güncelleniyor...',

  waiterService: 'Garson Hizmeti',
  premiumService: 'Premium servis deneyimi',
  seniorWaiter: 'Kıdemli Servis Uzmanı',
  reviews: 'değerlendirme',
  rateService: 'Hizmeti Değerlendirin',
  feedbackOptional: 'Geri bildiriminiz (İsteğe bağlı)...',
  send: 'Gönder',
  pleaseRate: 'Lütfen puan verin',
  thanksForRating: 'Değerlendirmeniz için teşekkürler!',
  callWaiter: 'Garson Çağır',
  requestBill: 'Hesap İste',
  viewBill: 'Adisyon görüntüle',
  specialRequest: 'Özel İstek',
  sendRequest: 'Talep gönder',

  waiterCallHero: 'Garson Çağır',
  waiterCallHeroDesc: 'Tek tıkla — neye ihtiyacınız var seçin',
  waiterCallReasonTitle: 'Size nasıl yardımcı olabiliriz?',
  waiterCallReasonSubtitle: 'Garson, talebinizden anında haberdar olur',
  waiterCallReasonWater: 'Su',
  waiterCallReasonBill: 'Hesap',
  waiterCallReasonHelp: 'Yardım',
  waiterCallReasonCleanup: 'Temizlik',
  waiterCallReasonMenu: 'Menü',
  waiterCallReasonOther: 'Diğer',
  waiterCallNoteLabel: 'Not (opsiyonel)',
  waiterCallNotePlaceholder: 'Detay eklemek isterseniz...',
  waiterCallSubmit: 'Garsonu Çağır',
  waiterCallSubmitting: 'Çağrılıyor...',
  waiterCallCancelByCustomer: 'Yanlışlıkla bastım',
  waiterCallCancelling: 'İptal ediliyor...',
  waiterCallClose: 'Kapat',
  waiterCallStatusPending: 'Garson çağrıldı',
  waiterCallStatusPendingDesc: 'Talebiniz bildirildi, lütfen bekleyin...',
  waiterCallStatusAcknowledged: 'Garson size doğru geliyor',
  waiterCallStatusAcknowledgedDesc: 'Talebiniz alındı, kısa süre içinde masanızda',
  waiterCallNotifiedWho: (name) => `${name} bilgilendirildi`,
  waiterCallNoAssigned: 'En yakın garson bilgilendirildi',
  waiterCallAcknowledgedBy: (name) => `${name} talebi aldı`,
  waiterCallAlreadyPending: 'Zaten çağırdınız — garson haberdar',
  waiterCallCreatedAgo: (label) => `${label} önce`,
  waiterCallTimeJustNow: 'şimdi',
  waiterCallTimeSeconds: (n) => `${n} sn`,
  waiterCallTimeMinutes: (n) => `${n} dk`,
  waiterCallTimeHours: (n) => `${n} sa`,
  waiterCallNoteShown: 'Notunuz',
  waiterCallErrorGeneric: 'Garson çağrılamadı, tekrar deneyin',
  waiterCallErrorThrottled: 'Çok sık deniyorsunuz, biraz bekleyin',
  waiterCallErrorNetwork: 'Bağlantı sorunu — tekrar deneyin',
  waiterCallToastSent: 'Garson çağrıldı!',
  waiterCallToastResolved: 'Talebiniz tamamlandı ✓',
  waiterCallToastCancelled: 'Çağrı iptal edildi',
  waiterCallLiveBadge: 'Canlı',
  waiterCallActionShortcut: 'Yardım gerek?',

  bill: 'Adisyon',
  completePayment: 'Ödemenizi tamamlayın',
  table_short: 'Masa',
  today: 'Bugün',
  noActiveOrder: 'Henüz aktif siparişiniz yok',
  pendingApproval: 'Onay bekliyor',
  subtotal: 'Ara Toplam',
  vat: 'KDV (%10)',
  grandTotal: 'Genel Toplam',
  paymentMethod: 'Ödeme Yöntemi',
  applePayUnavailable: 'Apple Pay Bu Cihazda Mevcut Değil',
  payWithApple: 'Apple Pay ile Öde',
  payWithCard: 'Bank Kartı ile Öde',
  processing: 'İşleniyor...',
  googlePayNotConfigured: 'Google Pay (Yapılandırılmamış)',

  liveBill: 'Canlı Adisyon',
  liveBillSubtitle: 'Masanızdaki tüm siparişler ve ödemeler',
  loadingBill: 'Hesap yükleniyor...',
  liveSyncedNow: 'Canlı senkronize',
  itemsTotal: 'Ürünler toplamı',
  timeChargesTotal: 'Süre ücreti',
  discountTotal: 'İndirim',
  prepaymentTotal: 'Ön ödeme',
  paymentTotal: 'Yapılan ödeme',
  netTotal: 'Net Toplam',
  payable: 'Ödenecek Tutar',
  fullyPaid: 'Tamamen ödendi ✓',
  noOrdersOnTable: 'Masada henüz onaylı siparişiniz yok',
  prepayment: 'Ön ödeme',
  partialPayment: 'Kısmi ödeme',
  manualDiscount: 'İndirim',
  timeChargeLabel: 'Süre ücreti',
  kitchenStatusReady: 'Hazır',
  kitchenStatusPreparing: 'Hazırlanıyor',
  kitchenStatusPending: 'Bekliyor',
  pieces: 'ad',

  enterPhone: 'Telefon numaranızı girin',
  enterPhoneSubtitle: 'Sadakat puanlarınızı ve geçmişinizi görmek için',
  phoneInputLabel: 'Telefon',
  continueAction: 'Devam Et',
  continueAsGuest: 'Misafir olarak devam et',
  phoneNotFound: 'Bu numara ile kayıt bulunamadı. Misafir olarak devam edebilirsiniz.',
  phoneInvalidFormat: 'Doğru format: +994 50 ...',
  forgetMe: 'Beni unut',
  identifying: 'Aranıyor...',
  customerHello: (name) => `Merhaba, ${name}`,

  loyaltyTitle: 'Sadakat Puanları',
  loyaltyBalance: 'Mevcut bakiye',
  loyaltyTotalEarned: 'Toplam kazanılan',
  loyaltyTotalSpent: 'Toplam kullanılan',
  loyaltyValueIn: 'Para değeri',
  lifetimeStats: 'Müşteri istatistikleri',
  lifetimeOrders: 'Toplam sipariş',
  lifetimeSpent: 'Toplam harcama',
  firstVisit: 'İlk ziyaret',
  lastVisit: 'Son ziyaret',
  customerBalance: 'Hesap bakiyesi',

  historyTitle: 'Sipariş Geçmişi',
  historySubtitle: 'Bu restorandaki önceki siparişleriniz',
  viewHistory: 'Geçmişi göster',
  hideHistory: 'Gizle',
  loadMore: 'Daha fazla yükle',
  loadingHistory: 'Geçmiş yükleniyor...',
  historyEmpty: 'Bu restoranda geçmiş siparişiniz yok',
  paidLabel: 'Ödendi',
  paymentMethodCash: 'Nakit',
  paymentMethodBank: 'Banka',
  paymentMethodCustomerBalance: 'Bakiye',
  prevPage: 'Önceki',
  nextPage: 'Sonraki',
  pageOf: (c, l) => `${c} / ${l}`,
  itemsCount: (n) => `${n} ürün`,

  myCart: 'Sepetim',
  productsCount: (n) => `${n} ürün`,
  emptyCartTitle: 'Sepetiniz boş',
  emptyCartSubtitle: 'Lezzetli yemekleri keşfedin',
  total: 'Toplam',
  selected: 'Seçilen',
  paySplit: 'Hesabı Birlikte Öde',
  cancelSelection: 'Seçimi İptal Et',
  sendOrder: 'Siparişi Gönder',
  paySelected: 'Seçilenleri Öde',
  cartEmpty: 'Sepet boş',

  chefSpecial: 'Şefin Özel Tarifi',
  chefSpecialDesc: 'Bu restoranın en çok tercih edilen lezzetlerinden biri.',
  qualityIngredients: 'Kaliteli İçerik',
  qualityIngredientsDesc: 'Taze, günlük ve premium malzemelerle hazırlanır.',
  selectSize: 'Boyut Seçin',
  meatType: 'Et Tipi',
  standard: 'Standart',
  extras: 'Ekstra Malzemeler',
  oftenBoughtTogether: 'Sıklıkla Birlikte Alınan',
  popular: 'POPÜLER',
  newBadge: 'YENİ',
  addToCart: 'Sepete Ekle',
  itemsAddedToCart: (n, name) => `${n} adet ${name} sepete eklendi`,
  itemAddedToCart: (name) => `${name} sepete eklendi`,

  meatChicken: 'Tavuk Eti',
  meatBeef: 'Dana Eti',
  meatLamb: 'Kuzu Eti',
  extraCheese: 'Ekstra Peynir',
  extraAvocado: 'Avokado',
  extraBacon: 'Bacon',
  extraMushroom: 'Mantar',

  rezmoPoints: 'Rezmo Puan',
  points: 'puan',
  pointsCallout: 'Sıradaki kahveniz bizden! 250 puan daha kazanın.',
  guest: 'Misafir',
  premiumMember: 'Premium Üye',
  signOut: 'Çıkış Yap',
  signedOut: 'Çıkış yapıldı',
  loggedInAsGoogle: 'Google ile giriş yapıldı',
  loggedInAsApple: 'Apple ile giriş yapıldı',
  subscriptionActive: 'Aboneliğiniz Aktif',
  dontMissDeals: 'Fırsatları Kaçırma',
  dontMissDealsDesc: 'Özel indirimler ve size özel kampanyalar',
  continueWithGoogle: 'Google ile Devam Et',
  continueWithApple: 'Apple ile Devam Et',
  connecting: 'Bağlanıyor...',
  or: 'veya',
  phonePlaceholder: '555 000 0000',
  invalidPhone: 'Lütfen geçerli bir telefon numarası girin.',
  subscribeSuccess: 'Aboneliğiniz başarıyla oluşturuldu!',
  welcomeBack: (name) => `Hoş geldiniz, ${name}!`,
  darkMode: 'Gece Modu',
  appSettings: 'Uygulama Ayarları',
  promoCodes: 'Promosyon Kodları',

  feedbackTitle: 'Özel İstek & Geri Bildirim',
  feedbackSubtitle: 'Görüşleriniz bizim için değerli',
  overallRating: 'Genel Değerlendirme',
  satisfied: 'Memnunum',
  notSatisfied: 'Memnun Değilim',
  selectCategory: 'Kategori Seçin',
  catComplaint: 'Şikayet',
  catSuggestion: 'Öneri',
  catFood: 'Yemek Hakkında',
  catService: 'Servis',
  catPayment: 'Ödeme',
  catOther: 'Diğer',
  quickOptions: 'Hızlı Seçenekler',
  qSlowService: 'Servis çok yavaş',
  qColdFood: 'Yemek soğuk geldi',
  qWrongOrder: 'Yanlış sipariş geldi',
  qDirtyTable: 'Masa temiz değil',
  qNoise: 'Ortam çok gürültülü',
  qSmallPortion: 'Porsiyon küçük',
  detailedDesc: 'Detaylı Açıklama (Opsiyonel)',
  detailedPlaceholder: 'Lütfen detaylı olarak açıklayın...',
  maxChars: 'Maksimum 500 karakter',
  feedbackEmptyError: 'Lütfen en az bir seçenek belirtin',
  feedbackThanks: 'Geri bildiriminiz alındı. Teşekkür ederiz!',

  notifAddedToCart: 'Sepete Eklendi',
  notifWaiterCalled: 'Garson Çağrıldı',
  notifWaiterCalledTitle: 'Garson Çağrıldı!',
  notifWaiterDesc: 'Sizinle ilgileneceğiz',
  notifWaiterOnTheWay: 'Garson yolda!',
  notifWaiterOnTheWayDesc: 'Kısa süre içinde masanıza gelecek',
  notifQuantity: 'Miktar',
  notifAddedToCartTitle: 'Sepete Eklendi!',

  cartEmptyToast: 'Sepetiniz boş',
  orderSent: 'Siparişiniz gönderildi!',
  orderFailed: 'Sipariş gönderilemedi. Tekrar deneyin.',
  paymentLinkFailed: 'Ödeme linki oluşturulamadı',
  paymentError: 'Ödeme hatası',
  paymentSuccess: 'Ödeme başarıyla tamamlandı!',
  applePayUnavailableMsg: 'Apple Pay bu cihazda kullanılamıyor',
  applePayValidationFailed: 'Apple Pay merchant doğrulaması başarısız',
  applePayNotConfigured: 'Apple Pay henüz tam yapılandırılmamış',
  applePayCannotComplete: 'Ödeme tamamlanamadı',
  applePayProcessFailed: 'Apple Pay işlemi başarısız',
  applePayStartFailed: 'Apple Pay başlatılamadı',
  googlePayError: 'Google Pay hatası',
  rezmoPointsCallout: 'Sıradaki kahveniz bizden!',
  wifi: 'WiFi',
  showHiddenCards: 'Gizlenen Kartları Göster',
});

/* ---------------- EN ---------------- */
const en: Strings = make({
  welcome: 'Welcome',
  welcomeDescription:
    'Order directly from here and pay easily.',
  quickOrder: 'Quick Order',
  quickOrderDesc: 'Directly to the kitchen',
  specialFlavors: 'Special Flavors',
  specialFlavorsDesc: "Chef's recommendations",
  viewMenu: 'View Menu',
  table: 'TABLE',

  search: 'Search',
  back: 'Back',
  profile: 'Profile',
  openUntil: 'Open until 23:00',

  all: 'All',
  chefRecommendations: "Chef's Recommendations",
  allFlavors: 'All Items',
  flavors: 'Items',
  productsNotFound: 'No products found',
  sets: 'Sets',
  setMenu: 'Set Menu',

  home: 'Home',
  waiter: 'Waiter',
  tracking: 'Tracking',
  history: 'Bill',

  orderTracking: 'Order Tracking',
  orderStatus: 'Status of your order',
  orderNumber: 'Order No',
  preparingInKitchen: 'Preparing in Kitchen',
  servedDone: 'Served ✓',
  estimated: 'Estimated',
  orderReceived: 'Order Received',
  preparing: 'Preparing',
  served: 'Served',
  completed: 'Completed',
  rightNow: 'Right now',
  enjoy: 'Enjoy your meal!',
  viewOrderDetails: 'View Order Details',
  noActiveOrderTrack: 'No order to track',
  noActiveOrderTrackDesc: 'When you have an active order, it will appear here in real time',
  goToMenu: 'Go to Menu',
  orderCanceled: 'Order Canceled',
  orderCanceledDesc: 'This order was canceled by the waiter',
  pendingApprovalLabel: 'Awaiting Approval',
  pendingApprovalDesc: 'Will be sent to the kitchen once the waiter approves',
  readyLabel: 'Ready - Awaiting Service',
  readyDesc: 'Kitchen has finished, waiter is bringing it to your table',
  almostReady: 'Almost ready',
  readyNow: 'Ready',
  itemsProgress: 'Items Progress',
  itemsList: 'Order Items',
  itemPending: 'Pending',
  itemPreparing: 'Preparing',
  itemReady: 'Ready',
  yourOrders: 'Your Orders',
  orderTime: 'Placed',
  refreshing: 'Refreshing...',

  waiterService: 'Waiter Service',
  premiumService: 'Premium service experience',
  seniorWaiter: 'Senior Service Specialist',
  reviews: 'reviews',
  rateService: 'Rate the service',
  feedbackOptional: 'Your feedback (optional)...',
  send: 'Send',
  pleaseRate: 'Please rate the service',
  thanksForRating: 'Thanks for your rating!',
  callWaiter: 'Call Waiter',
  requestBill: 'Request Bill',
  viewBill: 'View bill',
  specialRequest: 'Special Request',
  sendRequest: 'Send request',

  waiterCallHero: 'Call Waiter',
  waiterCallHeroDesc: 'One tap — pick what you need',
  waiterCallReasonTitle: 'How can we help?',
  waiterCallReasonSubtitle: 'The waiter is notified instantly',
  waiterCallReasonWater: 'Water',
  waiterCallReasonBill: 'Bill',
  waiterCallReasonHelp: 'Help',
  waiterCallReasonCleanup: 'Cleanup',
  waiterCallReasonMenu: 'Menu',
  waiterCallReasonOther: 'Other',
  waiterCallNoteLabel: 'Note (optional)',
  waiterCallNotePlaceholder: 'Add details if you want...',
  waiterCallSubmit: 'Call Waiter',
  waiterCallSubmitting: 'Calling...',
  waiterCallCancelByCustomer: 'Tapped by mistake',
  waiterCallCancelling: 'Cancelling...',
  waiterCallClose: 'Close',
  waiterCallStatusPending: 'Waiter called',
  waiterCallStatusPendingDesc: 'Your request was sent, please wait...',
  waiterCallStatusAcknowledged: 'Waiter is coming to you',
  waiterCallStatusAcknowledgedDesc: 'Your request was received, on the way',
  waiterCallNotifiedWho: (name) => `${name} has been notified`,
  waiterCallNoAssigned: 'The nearest waiter has been notified',
  waiterCallAcknowledgedBy: (name) => `${name} acknowledged your request`,
  waiterCallAlreadyPending: 'Already called — the waiter is aware',
  waiterCallCreatedAgo: (label) => `${label} ago`,
  waiterCallTimeJustNow: 'just now',
  waiterCallTimeSeconds: (n) => `${n}s`,
  waiterCallTimeMinutes: (n) => `${n}m`,
  waiterCallTimeHours: (n) => `${n}h`,
  waiterCallNoteShown: 'Your note',
  waiterCallErrorGeneric: 'Could not call waiter, try again',
  waiterCallErrorThrottled: 'Too many attempts, please wait a moment',
  waiterCallErrorNetwork: 'Network issue — try again',
  waiterCallToastSent: 'Waiter called!',
  waiterCallToastResolved: 'Request completed ✓',
  waiterCallToastCancelled: 'Call cancelled',
  waiterCallLiveBadge: 'Live',
  waiterCallActionShortcut: 'Need help?',

  bill: 'Bill',
  completePayment: 'Complete your payment',
  table_short: 'Table',
  today: 'Today',
  noActiveOrder: 'No active orders yet',
  pendingApproval: 'Awaiting approval',
  subtotal: 'Subtotal',
  vat: 'VAT (10%)',
  grandTotal: 'Grand Total',
  paymentMethod: 'Payment Method',
  applePayUnavailable: 'Apple Pay Not Available on This Device',
  payWithApple: 'Pay with Apple Pay',
  payWithCard: 'Pay with Bank Card',
  processing: 'Processing...',
  googlePayNotConfigured: 'Google Pay (Not Configured)',

  liveBill: 'Live Bill',
  liveBillSubtitle: 'All orders and payments on your table',
  loadingBill: 'Loading bill...',
  liveSyncedNow: 'Live synced',
  itemsTotal: 'Items total',
  timeChargesTotal: 'Time charges',
  discountTotal: 'Discount',
  prepaymentTotal: 'Prepayment',
  paymentTotal: 'Payments made',
  netTotal: 'Net Total',
  payable: 'Amount to Pay',
  fullyPaid: 'Fully paid ✓',
  noOrdersOnTable: 'No approved orders on this table yet',
  prepayment: 'Prepayment',
  partialPayment: 'Partial payment',
  manualDiscount: 'Discount',
  timeChargeLabel: 'Time charge',
  kitchenStatusReady: 'Ready',
  kitchenStatusPreparing: 'Preparing',
  kitchenStatusPending: 'Pending',
  pieces: 'pcs',

  enterPhone: 'Enter your phone number',
  enterPhoneSubtitle: 'To see your loyalty points and order history',
  phoneInputLabel: 'Phone',
  continueAction: 'Continue',
  continueAsGuest: 'Continue as Guest',
  phoneNotFound: 'No account found for this number. You can continue as a guest.',
  phoneInvalidFormat: 'Valid format: +994 50 ...',
  forgetMe: 'Forget me',
  identifying: 'Searching...',
  customerHello: (name) => `Hello, ${name}`,

  loyaltyTitle: 'Loyalty Points',
  loyaltyBalance: 'Current balance',
  loyaltyTotalEarned: 'Total earned',
  loyaltyTotalSpent: 'Total spent',
  loyaltyValueIn: 'Currency value',
  lifetimeStats: 'Customer stats',
  lifetimeOrders: 'Total orders',
  lifetimeSpent: 'Total spent',
  firstVisit: 'First visit',
  lastVisit: 'Last visit',
  customerBalance: 'Account balance',

  historyTitle: 'Order History',
  historySubtitle: 'Your previous orders at this restaurant',
  viewHistory: 'Show history',
  hideHistory: 'Hide',
  loadMore: 'Load more',
  loadingHistory: 'Loading history...',
  historyEmpty: 'No past orders at this restaurant',
  paidLabel: 'Paid',
  paymentMethodCash: 'Cash',
  paymentMethodBank: 'Bank',
  paymentMethodCustomerBalance: 'Balance',
  prevPage: 'Previous',
  nextPage: 'Next',
  pageOf: (c, l) => `${c} / ${l}`,
  itemsCount: (n) => `${n} item${n === 1 ? '' : 's'}`,

  myCart: 'My Cart',
  productsCount: (n) => `${n} item${n === 1 ? '' : 's'}`,
  emptyCartTitle: 'Your cart is empty',
  emptyCartSubtitle: 'Discover delicious food',
  total: 'Total',
  selected: 'Selected',
  paySplit: 'Pay Bill Together',
  cancelSelection: 'Cancel Selection',
  sendOrder: 'Send Order',
  paySelected: 'Pay Selected',
  cartEmpty: 'Cart empty',

  chefSpecial: "Chef's Special Recipe",
  chefSpecialDesc: "One of this restaurant's most preferred flavors.",
  qualityIngredients: 'Quality Ingredients',
  qualityIngredientsDesc: 'Prepared with fresh, daily, premium ingredients.',
  selectSize: 'Select Size',
  meatType: 'Meat Type',
  standard: 'Standard',
  extras: 'Extra Ingredients',
  oftenBoughtTogether: 'Often Bought Together',
  popular: 'POPULAR',
  newBadge: 'NEW',
  addToCart: 'Add to Cart',
  itemsAddedToCart: (n, name) => `${n} × ${name} added to cart`,
  itemAddedToCart: (name) => `${name} added to cart`,

  meatChicken: 'Chicken',
  meatBeef: 'Beef',
  meatLamb: 'Lamb',
  extraCheese: 'Extra Cheese',
  extraAvocado: 'Avocado',
  extraBacon: 'Bacon',
  extraMushroom: 'Mushroom',

  rezmoPoints: 'Rezmo Points',
  points: 'points',
  pointsCallout: 'Your next coffee is on us! Earn 250 more points.',
  guest: 'Guest',
  premiumMember: 'Premium Member',
  signOut: 'Sign Out',
  signedOut: 'Signed out',
  loggedInAsGoogle: 'Signed in with Google',
  loggedInAsApple: 'Signed in with Apple',
  subscriptionActive: 'Subscription Active',
  dontMissDeals: "Don't Miss Deals",
  dontMissDealsDesc: 'Special discounts and personalized offers',
  continueWithGoogle: 'Continue with Google',
  continueWithApple: 'Continue with Apple',
  connecting: 'Connecting...',
  or: 'or',
  phonePlaceholder: '555 000 0000',
  invalidPhone: 'Please enter a valid phone number.',
  subscribeSuccess: 'Subscription created successfully!',
  welcomeBack: (name) => `Welcome, ${name}!`,
  darkMode: 'Dark Mode',
  appSettings: 'App Settings',
  promoCodes: 'Promo Codes',

  feedbackTitle: 'Special Request & Feedback',
  feedbackSubtitle: 'Your opinion matters to us',
  overallRating: 'Overall Rating',
  satisfied: 'Satisfied',
  notSatisfied: 'Not Satisfied',
  selectCategory: 'Select Category',
  catComplaint: 'Complaint',
  catSuggestion: 'Suggestion',
  catFood: 'About Food',
  catService: 'Service',
  catPayment: 'Payment',
  catOther: 'Other',
  quickOptions: 'Quick Options',
  qSlowService: 'Service is too slow',
  qColdFood: 'Food arrived cold',
  qWrongOrder: 'Wrong order arrived',
  qDirtyTable: 'Table is not clean',
  qNoise: 'Place is too noisy',
  qSmallPortion: 'Portion is small',
  detailedDesc: 'Detailed Description (Optional)',
  detailedPlaceholder: 'Please describe in detail...',
  maxChars: 'Maximum 500 characters',
  feedbackEmptyError: 'Please select at least one option',
  feedbackThanks: 'Your feedback has been received. Thank you!',

  notifAddedToCart: 'Added to Cart',
  notifWaiterCalled: 'Waiter Called',
  notifWaiterCalledTitle: 'Waiter Called!',
  notifWaiterDesc: 'We will take care of you',
  notifWaiterOnTheWay: 'Waiter is on the way!',
  notifWaiterOnTheWayDesc: 'Will arrive at your table shortly',
  notifQuantity: 'Quantity',
  notifAddedToCartTitle: 'Added to Cart!',

  cartEmptyToast: 'Your cart is empty',
  orderSent: 'Your order has been sent!',
  orderFailed: 'Order could not be sent. Try again.',
  paymentLinkFailed: 'Could not create payment link',
  paymentError: 'Payment error',
  paymentSuccess: 'Payment completed successfully!',
  applePayUnavailableMsg: 'Apple Pay is not available on this device',
  applePayValidationFailed: 'Apple Pay merchant validation failed',
  applePayNotConfigured: 'Apple Pay is not fully configured yet',
  applePayCannotComplete: 'Payment could not be completed',
  applePayProcessFailed: 'Apple Pay process failed',
  applePayStartFailed: 'Apple Pay could not be started',
  googlePayError: 'Google Pay error',
  rezmoPointsCallout: 'Your next coffee is on us!',
  wifi: 'WiFi',
  showHiddenCards: 'Show Hidden Cards',
});

/* ---------------- RU ---------------- */
const ru: Strings = make({
  welcome: 'Добро пожаловать',
  welcomeDescription:
    'Закажите напрямую отсюда и легко оплатите.',
  quickOrder: 'Быстрый заказ',
  quickOrderDesc: 'Прямо на кухню',
  specialFlavors: 'Особые вкусы',
  specialFlavorsDesc: 'Рекомендации шеф-повара',
  viewMenu: 'Посмотреть меню',
  table: 'СТОЛ',

  search: 'Поиск',
  back: 'Назад',
  profile: 'Профиль',
  openUntil: 'Открыто до 23:00',

  all: 'Все',
  chefRecommendations: 'Рекомендации шефа',
  allFlavors: 'Все блюда',
  flavors: 'Блюда',
  productsNotFound: 'Товары не найдены',
  sets: 'Сеты',
  setMenu: 'Сет-меню',

  home: 'Главная',
  waiter: 'Официант',
  tracking: 'Отслеживание',
  history: 'Счёт',

  orderTracking: 'Отслеживание заказа',
  orderStatus: 'Статус вашего заказа',
  orderNumber: 'Заказ №',
  preparingInKitchen: 'Готовится на кухне',
  servedDone: 'Подано ✓',
  estimated: 'Примерно',
  orderReceived: 'Заказ принят',
  preparing: 'Готовится',
  served: 'Подано',
  completed: 'Завершено',
  rightNow: 'Сейчас',
  enjoy: 'Приятного аппетита!',
  viewOrderDetails: 'Посмотреть детали заказа',
  noActiveOrderTrack: 'Нет заказов для отслеживания',
  noActiveOrderTrackDesc: 'Когда у вас будет активный заказ, он появится здесь в реальном времени',
  goToMenu: 'Перейти к меню',
  orderCanceled: 'Заказ отменён',
  orderCanceledDesc: 'Этот заказ был отменён официантом',
  pendingApprovalLabel: 'Ожидает подтверждения',
  pendingApprovalDesc: 'Будет отправлен на кухню после подтверждения официантом',
  readyLabel: 'Готов - Ожидает подачи',
  readyDesc: 'Кухня закончила, официант несёт заказ к столу',
  almostReady: 'Почти готов',
  readyNow: 'Готов',
  itemsProgress: 'Готовность блюд',
  itemsList: 'Состав заказа',
  itemPending: 'В ожидании',
  itemPreparing: 'Готовится',
  itemReady: 'Готово',
  yourOrders: 'Ваши заказы',
  orderTime: 'Создан',
  refreshing: 'Обновление...',

  waiterService: 'Сервис официанта',
  premiumService: 'Премиальный опыт обслуживания',
  seniorWaiter: 'Старший специалист',
  reviews: 'отзывов',
  rateService: 'Оцените сервис',
  feedbackOptional: 'Ваш отзыв (необязательно)...',
  send: 'Отправить',
  pleaseRate: 'Пожалуйста, поставьте оценку',
  thanksForRating: 'Спасибо за вашу оценку!',
  callWaiter: 'Позвать официанта',
  requestBill: 'Запросить счёт',
  viewBill: 'Посмотреть счёт',
  specialRequest: 'Особый запрос',
  sendRequest: 'Отправить запрос',

  waiterCallHero: 'Позвать официанта',
  waiterCallHeroDesc: 'Одно касание — выберите, что вам нужно',
  waiterCallReasonTitle: 'Чем мы можем помочь?',
  waiterCallReasonSubtitle: 'Официант получит уведомление мгновенно',
  waiterCallReasonWater: 'Вода',
  waiterCallReasonBill: 'Счёт',
  waiterCallReasonHelp: 'Помощь',
  waiterCallReasonCleanup: 'Уборка',
  waiterCallReasonMenu: 'Меню',
  waiterCallReasonOther: 'Другое',
  waiterCallNoteLabel: 'Примечание (необязательно)',
  waiterCallNotePlaceholder: 'Добавьте детали, если хотите...',
  waiterCallSubmit: 'Позвать официанта',
  waiterCallSubmitting: 'Вызов...',
  waiterCallCancelByCustomer: 'Нажал по ошибке',
  waiterCallCancelling: 'Отмена...',
  waiterCallClose: 'Закрыть',
  waiterCallStatusPending: 'Официант вызван',
  waiterCallStatusPendingDesc: 'Запрос отправлен, пожалуйста, подождите...',
  waiterCallStatusAcknowledged: 'Официант идёт к вам',
  waiterCallStatusAcknowledgedDesc: 'Запрос принят, скоро будет у стола',
  waiterCallNotifiedWho: (name) => `${name} получил уведомление`,
  waiterCallNoAssigned: 'Уведомлён ближайший официант',
  waiterCallAcknowledgedBy: (name) => `${name} принял запрос`,
  waiterCallAlreadyPending: 'Уже вызвали — официант в курсе',
  waiterCallCreatedAgo: (label) => `${label} назад`,
  waiterCallTimeJustNow: 'сейчас',
  waiterCallTimeSeconds: (n) => `${n} с`,
  waiterCallTimeMinutes: (n) => `${n} мин`,
  waiterCallTimeHours: (n) => `${n} ч`,
  waiterCallNoteShown: 'Ваша заметка',
  waiterCallErrorGeneric: 'Не удалось вызвать, попробуйте ещё раз',
  waiterCallErrorThrottled: 'Слишком часто, подождите немного',
  waiterCallErrorNetwork: 'Проблема с сетью — попробуйте снова',
  waiterCallToastSent: 'Официант вызван!',
  waiterCallToastResolved: 'Запрос выполнен ✓',
  waiterCallToastCancelled: 'Вызов отменён',
  waiterCallLiveBadge: 'Live',
  waiterCallActionShortcut: 'Нужна помощь?',

  bill: 'Счёт',
  completePayment: 'Завершите оплату',
  table_short: 'Стол',
  today: 'Сегодня',
  noActiveOrder: 'Активных заказов пока нет',
  pendingApproval: 'Ожидает подтверждения',
  subtotal: 'Подытог',
  vat: 'НДС (10%)',
  grandTotal: 'Итого',
  paymentMethod: 'Способ оплаты',
  applePayUnavailable: 'Apple Pay недоступен на этом устройстве',
  payWithApple: 'Оплатить через Apple Pay',
  payWithCard: 'Оплатить картой',
  processing: 'Обработка...',
  googlePayNotConfigured: 'Google Pay (не настроен)',

  liveBill: 'Живой счёт',
  liveBillSubtitle: 'Все заказы и платежи за вашим столом',
  loadingBill: 'Загрузка счёта...',
  liveSyncedNow: 'Синхронизировано',
  itemsTotal: 'Сумма по позициям',
  timeChargesTotal: 'Оплата за время',
  discountTotal: 'Скидка',
  prepaymentTotal: 'Предоплата',
  paymentTotal: 'Оплачено',
  netTotal: 'Чистая сумма',
  payable: 'К оплате',
  fullyPaid: 'Полностью оплачено ✓',
  noOrdersOnTable: 'На столе пока нет подтверждённых заказов',
  prepayment: 'Предоплата',
  partialPayment: 'Частичная оплата',
  manualDiscount: 'Скидка',
  timeChargeLabel: 'Оплата за время',
  kitchenStatusReady: 'Готово',
  kitchenStatusPreparing: 'Готовится',
  kitchenStatusPending: 'В ожидании',
  pieces: 'шт',

  enterPhone: 'Введите номер телефона',
  enterPhoneSubtitle: 'Чтобы увидеть бонусы и историю заказов',
  phoneInputLabel: 'Телефон',
  continueAction: 'Продолжить',
  continueAsGuest: 'Продолжить как гость',
  phoneNotFound: 'По этому номеру нет регистрации. Можно продолжить как гость.',
  phoneInvalidFormat: 'Правильный формат: +994 50 ...',
  forgetMe: 'Забыть меня',
  identifying: 'Поиск...',
  customerHello: (name) => `Здравствуйте, ${name}`,

  loyaltyTitle: 'Бонусные баллы',
  loyaltyBalance: 'Текущий баланс',
  loyaltyTotalEarned: 'Всего начислено',
  loyaltyTotalSpent: 'Всего потрачено',
  loyaltyValueIn: 'Денежный эквивалент',
  lifetimeStats: 'Статистика клиента',
  lifetimeOrders: 'Всего заказов',
  lifetimeSpent: 'Всего потрачено',
  firstVisit: 'Первый визит',
  lastVisit: 'Последний визит',
  customerBalance: 'Баланс счёта',

  historyTitle: 'История заказов',
  historySubtitle: 'Ваши прошлые заказы в этом ресторане',
  viewHistory: 'Показать историю',
  hideHistory: 'Скрыть',
  loadMore: 'Загрузить ещё',
  loadingHistory: 'Загрузка истории...',
  historyEmpty: 'Нет прошлых заказов в этом ресторане',
  paidLabel: 'Оплачено',
  paymentMethodCash: 'Наличные',
  paymentMethodBank: 'Карта',
  paymentMethodCustomerBalance: 'Баланс',
  prevPage: 'Назад',
  nextPage: 'Вперёд',
  pageOf: (c, l) => `${c} / ${l}`,
  itemsCount: (n) => `${n} ${n === 1 ? 'позиция' : n < 5 ? 'позиции' : 'позиций'}`,

  myCart: 'Моя корзина',
  productsCount: (n) => `${n} ${n === 1 ? 'товар' : n < 5 ? 'товара' : 'товаров'}`,
  emptyCartTitle: 'Ваша корзина пуста',
  emptyCartSubtitle: 'Откройте для себя вкусные блюда',
  total: 'Итого',
  selected: 'Выбрано',
  paySplit: 'Оплатить вместе',
  cancelSelection: 'Отменить выбор',
  sendOrder: 'Отправить заказ',
  paySelected: 'Оплатить выбранные',
  cartEmpty: 'Корзина пуста',

  chefSpecial: 'Особый рецепт шефа',
  chefSpecialDesc: 'Один из самых популярных вкусов этого ресторана.',
  qualityIngredients: 'Качественные ингредиенты',
  qualityIngredientsDesc: 'Готовится из свежих, ежедневных, премиальных ингредиентов.',
  selectSize: 'Выберите размер',
  meatType: 'Тип мяса',
  standard: 'Стандарт',
  extras: 'Дополнительно',
  oftenBoughtTogether: 'Часто покупают вместе',
  popular: 'ПОПУЛЯРНО',
  newBadge: 'НОВОЕ',
  addToCart: 'В корзину',
  itemsAddedToCart: (n, name) => `${n} × ${name} добавлено в корзину`,
  itemAddedToCart: (name) => `${name} добавлено в корзину`,

  meatChicken: 'Курица',
  meatBeef: 'Говядина',
  meatLamb: 'Баранина',
  extraCheese: 'Доп. сыр',
  extraAvocado: 'Авокадо',
  extraBacon: 'Бекон',
  extraMushroom: 'Грибы',

  rezmoPoints: 'Баллы Rezmo',
  points: 'баллов',
  pointsCallout: 'Следующий кофе за наш счёт! Заработайте ещё 250 баллов.',
  guest: 'Гость',
  premiumMember: 'Премиум-участник',
  signOut: 'Выйти',
  signedOut: 'Вы вышли',
  loggedInAsGoogle: 'Вход через Google',
  loggedInAsApple: 'Вход через Apple',
  subscriptionActive: 'Подписка активна',
  dontMissDeals: 'Не пропустите акции',
  dontMissDealsDesc: 'Специальные скидки и персональные предложения',
  continueWithGoogle: 'Продолжить с Google',
  continueWithApple: 'Продолжить с Apple',
  connecting: 'Подключение...',
  or: 'или',
  phonePlaceholder: '555 000 0000',
  invalidPhone: 'Введите корректный номер телефона.',
  subscribeSuccess: 'Подписка успешно оформлена!',
  welcomeBack: (name) => `Добро пожаловать, ${name}!`,
  darkMode: 'Тёмный режим',
  appSettings: 'Настройки приложения',
  promoCodes: 'Промокоды',

  feedbackTitle: 'Особый запрос и отзыв',
  feedbackSubtitle: 'Ваше мнение для нас важно',
  overallRating: 'Общая оценка',
  satisfied: 'Доволен',
  notSatisfied: 'Не доволен',
  selectCategory: 'Выберите категорию',
  catComplaint: 'Жалоба',
  catSuggestion: 'Предложение',
  catFood: 'О еде',
  catService: 'Сервис',
  catPayment: 'Оплата',
  catOther: 'Другое',
  quickOptions: 'Быстрые варианты',
  qSlowService: 'Сервис слишком медленный',
  qColdFood: 'Еда пришла холодной',
  qWrongOrder: 'Пришёл неверный заказ',
  qDirtyTable: 'Стол не чистый',
  qNoise: 'Слишком шумно',
  qSmallPortion: 'Маленькая порция',
  detailedDesc: 'Подробное описание (необязательно)',
  detailedPlaceholder: 'Опишите подробно...',
  maxChars: 'Максимум 500 символов',
  feedbackEmptyError: 'Выберите хотя бы один вариант',
  feedbackThanks: 'Ваш отзыв принят. Спасибо!',

  notifAddedToCart: 'Добавлено в корзину',
  notifWaiterCalled: 'Официант вызван',
  notifWaiterCalledTitle: 'Официант вызван!',
  notifWaiterDesc: 'Мы вами займёмся',
  notifWaiterOnTheWay: 'Официант идёт!',
  notifWaiterOnTheWayDesc: 'Скоро подойдёт к вашему столу',
  notifQuantity: 'Количество',
  notifAddedToCartTitle: 'Добавлено в корзину!',

  cartEmptyToast: 'Ваша корзина пуста',
  orderSent: 'Ваш заказ отправлен!',
  orderFailed: 'Не удалось отправить заказ. Попробуйте снова.',
  paymentLinkFailed: 'Не удалось создать ссылку оплаты',
  paymentError: 'Ошибка оплаты',
  paymentSuccess: 'Оплата успешно завершена!',
  applePayUnavailableMsg: 'Apple Pay недоступен на этом устройстве',
  applePayValidationFailed: 'Не удалось пройти проверку продавца Apple Pay',
  applePayNotConfigured: 'Apple Pay ещё не полностью настроен',
  applePayCannotComplete: 'Оплата не была завершена',
  applePayProcessFailed: 'Сбой процесса Apple Pay',
  applePayStartFailed: 'Не удалось запустить Apple Pay',
  googlePayError: 'Ошибка Google Pay',
  rezmoPointsCallout: 'Следующий кофе за наш счёт!',
  wifi: 'Wi-Fi',
  showHiddenCards: 'Показать скрытые карты',
});

/* ---------------- AZ ---------------- */
const az: Strings = make({
  welcome: 'Xoş gəlmisiniz',
  welcomeDescription:
    'Sifarişinizi birbaşa buradan verə və asanlıqla ödəyə bilərsiniz.',
  quickOrder: 'Sürətli sifariş',
  quickOrderDesc: 'Birbaşa mətbəxə',
  specialFlavors: 'Xüsusi dadlar',
  specialFlavorsDesc: 'Şef-aşbazın tövsiyələri',
  viewMenu: 'Menyuya bax',
  table: 'MASA',

  search: 'Axtar',
  back: 'Geri',
  profile: 'Profil',
  openUntil: '23:00-a qədər açıqdır',

  all: 'Hamısı',
  chefRecommendations: 'Şefin tövsiyələri',
  allFlavors: 'Bütün dadlar',
  flavors: 'Dadlar',
  productsNotFound: 'Məhsul tapılmadı',
  sets: 'Setlər',
  setMenu: 'Set Menyu',

  home: 'Ana səhifə',
  waiter: 'Ofisiant',
  tracking: 'İzləmə',
  history: 'Hesab',

  orderTracking: 'Sifariş İzləməsi',
  orderStatus: 'Sifarişinizin statusu',
  orderNumber: 'Sifariş №',
  preparingInKitchen: 'Mətbəxdə hazırlanır',
  servedDone: 'Təqdim edildi ✓',
  estimated: 'Təxmini',
  orderReceived: 'Sifariş qəbul edildi',
  preparing: 'Hazırlanır',
  served: 'Təqdim edildi',
  completed: 'Tamamlandı',
  rightNow: 'İndi',
  enjoy: 'Nuş olsun!',
  viewOrderDetails: 'Sifariş detallarına bax',
  noActiveOrderTrack: 'İzləniləcək sifariş yoxdur',
  noActiveOrderTrackDesc: 'Aktiv sifarişiniz olduqda burada canlı görünəcək',
  goToMenu: 'Menyuya keç',
  orderCanceled: 'Sifariş Ləğv Edildi',
  orderCanceledDesc: 'Bu sifariş ofisiant tərəfindən ləğv edildi',
  pendingApprovalLabel: 'Təsdiq gözləyir',
  pendingApprovalDesc: 'Ofisiant təsdiqlədikdən sonra mətbəxə göndəriləcək',
  readyLabel: 'Hazırdır - Servis gözləyir',
  readyDesc: 'Mətbəx hazırladı, ofisiant masanıza gətirir',
  almostReady: 'Hazır olmaqa az qaldı',
  readyNow: 'Hazırdır',
  itemsProgress: 'Məhsulların hazırlığı',
  itemsList: 'Sifariş tərkibi',
  itemPending: 'Gözləyir',
  itemPreparing: 'Hazırlanır',
  itemReady: 'Hazırdır',
  yourOrders: 'Sifarişləriniz',
  orderTime: 'Verildi',
  refreshing: 'Yenilənir...',

  waiterService: 'Ofisiant Xidməti',
  premiumService: 'Premium xidmət təcrübəsi',
  seniorWaiter: 'Baş Xidmət Mütəxəssisi',
  reviews: 'rəy',
  rateService: 'Xidməti qiymətləndirin',
  feedbackOptional: 'Rəyiniz (istəyə bağlı)...',
  send: 'Göndər',
  pleaseRate: 'Zəhmət olmasa qiymətləndirin',
  thanksForRating: 'Qiymətləndirməniz üçün təşəkkürlər!',
  callWaiter: 'Ofisiant çağır',
  requestBill: 'Hesab istə',
  viewBill: 'Hesabı gör',
  specialRequest: 'Xüsusi istək',
  sendRequest: 'İstək göndər',

  waiterCallHero: 'Ofisiant çağır',
  waiterCallHeroDesc: 'Bir kliklə — nəyə ehtiyacınız var seçin',
  waiterCallReasonTitle: 'Sizə necə kömək edə bilərik?',
  waiterCallReasonSubtitle: 'Ofisiant tələbinizdən dərhal xəbərdar olur',
  waiterCallReasonWater: 'Su',
  waiterCallReasonBill: 'Hesab',
  waiterCallReasonHelp: 'Kömək',
  waiterCallReasonCleanup: 'Təmizlik',
  waiterCallReasonMenu: 'Menyu',
  waiterCallReasonOther: 'Digər',
  waiterCallNoteLabel: 'Qeyd (istəyə bağlı)',
  waiterCallNotePlaceholder: 'Detal əlavə etmək istəsəniz...',
  waiterCallSubmit: 'Ofisiantı çağır',
  waiterCallSubmitting: 'Çağırılır...',
  waiterCallCancelByCustomer: 'Səhvən bastım',
  waiterCallCancelling: 'Ləğv edilir...',
  waiterCallClose: 'Bağla',
  waiterCallStatusPending: 'Ofisiant çağırıldı',
  waiterCallStatusPendingDesc: 'Tələbiniz bildirildi, gözləyin...',
  waiterCallStatusAcknowledged: 'Ofisiant sizə doğru gəlir',
  waiterCallStatusAcknowledgedDesc: 'Tələbiniz qəbul edildi, qısa müddətdə masanızda',
  waiterCallNotifiedWho: (name) => `${name} xəbərdar edildi`,
  waiterCallNoAssigned: 'Ən yaxın ofisiant xəbərdar edildi',
  waiterCallAcknowledgedBy: (name) => `${name} tələbi qəbul etdi`,
  waiterCallAlreadyPending: 'Artıq çağırılıb — ofisiant xəbərdardır',
  waiterCallCreatedAgo: (label) => `${label} əvvəl`,
  waiterCallTimeJustNow: 'indi',
  waiterCallTimeSeconds: (n) => `${n} sn`,
  waiterCallTimeMinutes: (n) => `${n} dəq`,
  waiterCallTimeHours: (n) => `${n} saat`,
  waiterCallNoteShown: 'Qeydiniz',
  waiterCallErrorGeneric: 'Ofisiant çağırıla bilmədi, yenidən cəhd edin',
  waiterCallErrorThrottled: 'Çox tez-tez basırsınız, bir az gözləyin',
  waiterCallErrorNetwork: 'Bağlantı problemi — yenidən cəhd edin',
  waiterCallToastSent: 'Ofisiant çağırıldı!',
  waiterCallToastResolved: 'Tələbiniz tamamlandı ✓',
  waiterCallToastCancelled: 'Çağırış ləğv edildi',
  waiterCallLiveBadge: 'Canlı',
  waiterCallActionShortcut: 'Kömək lazımdır?',

  bill: 'Hesab',
  completePayment: 'Ödənişinizi tamamlayın',
  table_short: 'Masa',
  today: 'Bu gün',
  noActiveOrder: 'Hələ aktiv sifarişiniz yoxdur',
  pendingApproval: 'Təsdiq gözləyir',
  subtotal: 'Ara cəm',
  vat: 'ƏDV (10%)',
  grandTotal: 'Ümumi məbləğ',
  paymentMethod: 'Ödəniş üsulu',
  applePayUnavailable: 'Apple Pay bu cihazda mövcud deyil',
  payWithApple: 'Apple Pay ilə ödə',
  payWithCard: 'Bank kartı ilə ödə',
  processing: 'Emal olunur...',
  googlePayNotConfigured: 'Google Pay (Konfiqurasiya edilməyib)',

  liveBill: 'Canlı Adisyon',
  liveBillSubtitle: 'Masanızdakı bütün sifarişlər və ödənişlər',
  loadingBill: 'Hesab yüklənir...',
  liveSyncedNow: 'Canlı sinxron',
  itemsTotal: 'Məhsulların cəmi',
  timeChargesTotal: 'Vaxt ödənişi',
  discountTotal: 'Endirim',
  prepaymentTotal: 'Ön ödəniş',
  paymentTotal: 'Edilmiş ödəniş',
  netTotal: 'Net cəm',
  payable: 'Ödəniləcək məbləğ',
  fullyPaid: 'Tamamilə ödənilib ✓',
  noOrdersOnTable: 'Masada hələ təsdiqlənmiş sifarişiniz yoxdur',
  prepayment: 'Ön ödəniş',
  partialPayment: 'Qismi ödəniş',
  manualDiscount: 'Endirim',
  timeChargeLabel: 'Vaxt ödənişi',
  kitchenStatusReady: 'Hazır',
  kitchenStatusPreparing: 'Hazırlanır',
  kitchenStatusPending: 'Gözləyir',
  pieces: 'ədəd',

  enterPhone: 'Telefon nömrənizi daxil edin',
  enterPhoneSubtitle: 'Loyallıq xallarınızı və sifariş tarixçənizi görmək üçün',
  phoneInputLabel: 'Telefon',
  continueAction: 'Davam et',
  continueAsGuest: 'Qonaq olaraq davam et',
  phoneNotFound: 'Bu nömrə ilə qeydiyyat tapılmadı. Qonaq olaraq davam edə bilərsiniz.',
  phoneInvalidFormat: 'Düzgün format: +994 50 ...',
  forgetMe: 'Məni unut',
  identifying: 'Axtarılır...',
  customerHello: (name) => `Salam, ${name}`,

  loyaltyTitle: 'Loyallıq xalları',
  loyaltyBalance: 'Cari balans',
  loyaltyTotalEarned: 'Toplam qazanılan',
  loyaltyTotalSpent: 'Toplam istifadə olunan',
  loyaltyValueIn: 'Pul dəyəri',
  lifetimeStats: 'Müştəri statistikası',
  lifetimeOrders: 'Toplam sifariş',
  lifetimeSpent: 'Toplam xərclənən',
  firstVisit: 'İlk ziyarət',
  lastVisit: 'Son ziyarət',
  customerBalance: 'Hesab balansı',

  historyTitle: 'Sifariş tarixçəsi',
  historySubtitle: 'Bu restorandakı keçmiş sifarişləriniz',
  viewHistory: 'Tarixçəni göstər',
  hideHistory: 'Gizlət',
  loadMore: 'Daha çox yüklə',
  loadingHistory: 'Tarixçə yüklənir...',
  historyEmpty: 'Bu restoranda keçmiş sifarişiniz yoxdur',
  paidLabel: 'Ödənilib',
  paymentMethodCash: 'Nağd',
  paymentMethodBank: 'Bank',
  paymentMethodCustomerBalance: 'Balans',
  prevPage: 'Əvvəlki',
  nextPage: 'Sonrakı',
  pageOf: (c, l) => `${c} / ${l}`,
  itemsCount: (n) => `${n} məhsul`,

  myCart: 'Səbətim',
  productsCount: (n) => `${n} məhsul`,
  emptyCartTitle: 'Səbətiniz boşdur',
  emptyCartSubtitle: 'Dadlı yeməkləri kəşf edin',
  total: 'Cəmi',
  selected: 'Seçilmiş',
  paySplit: 'Hesabı birlikdə ödə',
  cancelSelection: 'Seçimi ləğv et',
  sendOrder: 'Sifariş göndər',
  paySelected: 'Seçilmişləri ödə',
  cartEmpty: 'Səbət boşdur',

  chefSpecial: 'Şefin xüsusi reseptidir',
  chefSpecialDesc: 'Bu restoranın ən çox seçilən dadlarından biridir.',
  qualityIngredients: 'Keyfiyyətli tərkib',
  qualityIngredientsDesc: 'Təzə, gündəlik və premium tərkib hissələri ilə hazırlanır.',
  selectSize: 'Ölçü seçin',
  meatType: 'Ət növü',
  standard: 'Standart',
  extras: 'Əlavə tərkib',
  oftenBoughtTogether: 'Tez-tez birlikdə alınanlar',
  popular: 'POPULYAR',
  newBadge: 'YENİ',
  addToCart: 'Səbətə əlavə et',
  itemsAddedToCart: (n, name) => `${n} × ${name} səbətə əlavə edildi`,
  itemAddedToCart: (name) => `${name} səbətə əlavə edildi`,

  meatChicken: 'Toyuq əti',
  meatBeef: 'Mal əti',
  meatLamb: 'Quzu əti',
  extraCheese: 'Əlavə pendir',
  extraAvocado: 'Avokado',
  extraBacon: 'Bekon',
  extraMushroom: 'Göbələk',

  rezmoPoints: 'Rezmo Xal',
  points: 'xal',
  pointsCallout: 'Növbəti qəhvəniz bizdən! 250 xal daha qazanın.',
  guest: 'Qonaq',
  premiumMember: 'Premium üzv',
  signOut: 'Çıxış',
  signedOut: 'Çıxış edildi',
  loggedInAsGoogle: 'Google ilə daxil olundu',
  loggedInAsApple: 'Apple ilə daxil olundu',
  subscriptionActive: 'Abunəliyiniz aktivdir',
  dontMissDeals: 'Fürsətləri qaçırmayın',
  dontMissDealsDesc: 'Xüsusi endirimlər və sizə özəl kampaniyalar',
  continueWithGoogle: 'Google ilə davam et',
  continueWithApple: 'Apple ilə davam et',
  connecting: 'Qoşulur...',
  or: 'və ya',
  phonePlaceholder: '555 000 0000',
  invalidPhone: 'Lütfən düzgün telefon nömrəsi daxil edin.',
  subscribeSuccess: 'Abunəliyiniz uğurla yaradıldı!',
  welcomeBack: (name) => `Xoş gəlmisiniz, ${name}!`,
  darkMode: 'Gecə rejimi',
  appSettings: 'Tətbiq parametrləri',
  promoCodes: 'Promo kodlar',

  feedbackTitle: 'Xüsusi istək və rəy',
  feedbackSubtitle: 'Fikriniz bizim üçün dəyərlidir',
  overallRating: 'Ümumi qiymətləndirmə',
  satisfied: 'Razıyam',
  notSatisfied: 'Razı deyiləm',
  selectCategory: 'Kateqoriya seçin',
  catComplaint: 'Şikayət',
  catSuggestion: 'Təklif',
  catFood: 'Yemək haqqında',
  catService: 'Xidmət',
  catPayment: 'Ödəniş',
  catOther: 'Digər',
  quickOptions: 'Sürətli seçimlər',
  qSlowService: 'Xidmət çox yavaşdır',
  qColdFood: 'Yemək soyuq gəldi',
  qWrongOrder: 'Səhv sifariş gəldi',
  qDirtyTable: 'Masa təmiz deyil',
  qNoise: 'Mühit çox səs-küylüdür',
  qSmallPortion: 'Porsiya kiçikdir',
  detailedDesc: 'Ətraflı açıqlama (istəyə bağlı)',
  detailedPlaceholder: 'Lütfən ətraflı izah edin...',
  maxChars: 'Maksimum 500 simvol',
  feedbackEmptyError: 'Ən azı bir variant seçin',
  feedbackThanks: 'Rəyiniz qəbul edildi. Təşəkkür edirik!',

  notifAddedToCart: 'Səbətə əlavə edildi',
  notifWaiterCalled: 'Ofisiant çağırıldı',
  notifWaiterCalledTitle: 'Ofisiant çağırıldı!',
  notifWaiterDesc: 'Sizinlə maraqlanacağıq',
  notifWaiterOnTheWay: 'Ofisiant yoldadır!',
  notifWaiterOnTheWayDesc: 'Qısa müddətdə masanıza gələcək',
  notifQuantity: 'Miqdar',
  notifAddedToCartTitle: 'Səbətə əlavə edildi!',

  cartEmptyToast: 'Səbətiniz boşdur',
  orderSent: 'Sifarişiniz göndərildi!',
  orderFailed: 'Sifariş göndərilə bilmədi. Yenidən cəhd edin.',
  paymentLinkFailed: 'Ödəniş linki yaradıla bilmədi',
  paymentError: 'Ödəniş xətası',
  paymentSuccess: 'Ödəniş uğurla tamamlandı!',
  applePayUnavailableMsg: 'Apple Pay bu cihazda istifadə oluna bilmir',
  applePayValidationFailed: 'Apple Pay merchant doğrulaması uğursuz oldu',
  applePayNotConfigured: 'Apple Pay hələ tam konfiqurasiya edilməyib',
  applePayCannotComplete: 'Ödəniş tamamlana bilmədi',
  applePayProcessFailed: 'Apple Pay prosesi uğursuz oldu',
  applePayStartFailed: 'Apple Pay başladıla bilmədi',
  googlePayError: 'Google Pay xətası',
  rezmoPointsCallout: 'Növbəti qəhvəniz bizdən!',
  wifi: 'WiFi',
  showHiddenCards: 'Gizlənən Kartları Göstər',
});

const ALL: Record<QrLanguage, Strings> = { tr, en, ru, az };

interface LanguageContextType {
  language: QrLanguage;
  setLanguage: (lang: QrLanguage) => void;
  t: Strings;
}

const QrLanguageContext = createContext<LanguageContextType | undefined>(undefined);

const detectInitialLanguage = (): QrLanguage => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as QrLanguage | null;
    if (saved && ALL[saved]) return saved;
  } catch {
    /* ignore */
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const code = navigator.language.toLowerCase().slice(0, 2);
    if (code === 'tr') return 'tr';
    if (code === 'ru') return 'ru';
    if (code === 'az') return 'az';
    if (code === 'en') return 'en';
  }
  return 'tr';
};

export function QrLanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<QrLanguage>(() => detectInitialLanguage());

  const setLanguage = useCallback((lang: QrLanguage) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, []);

  // Update <html lang="..."> for screen readers / browser features
  useEffect(() => {
    try {
      document.documentElement.lang = language;
    } catch {
      /* ignore */
    }
  }, [language]);

  const value = useMemo<LanguageContextType>(
    () => ({ language, setLanguage, t: ALL[language] }),
    [language, setLanguage],
  );

  return (
    <QrLanguageContext.Provider value={value}>
      {children}
    </QrLanguageContext.Provider>
  );
}

export function useQrLanguage(): LanguageContextType {
  const ctx = useContext(QrLanguageContext);
  if (!ctx) {
    // Defensive fallback so a hook usage outside the provider does not crash
    // (gives the default Turkish translations).
    return {
      language: 'tr',
      setLanguage: () => {
        /* noop */
      },
      t: tr,
    };
  }
  return ctx;
}

/**
 * Imperative accessor for code that runs OUTSIDE a React component (e.g.
 * toast callbacks fired from a parent that hosts the provider). Reads the
 * persisted language from localStorage and falls back to the navigator/Turkish.
 *
 * Use sparingly - prefer `useQrLanguage()` inside components.
 */
export function getQrTranslations(): Strings {
  return ALL[detectInitialLanguage()];
}
